import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProjectShare } from '~/features/work/project-role';
import { api } from '~/lib/api-client';
import { graphKeys, workBoardKeys } from '~/lib/query-keys';
import { usePaginatedList } from '~/hooks/use-paginated-list';
import type { EntitySaveResponse } from '~/lib/entity-save-outcome';
import { MEMORY_ENTITY_TYPES } from '~/lib/entity-types';

/** Where a graph fact was learned (FRONT-377). Null fields = origin not recorded. */
export interface GraphProvenance {
  surface: string | null;
  sessionId: string | null;
  messageId: string | null;
}

export interface GraphEntity {
  id: string;
  type: string;
  name: string;
  attributes: Record<string, any>;
  relationships?: Array<{ label: string; target: string; targetId?: string; type?: string }>;
  pinned?: boolean;
  flagged?: boolean;
  source?: string;
  provenance?: GraphProvenance;
  created_at?: string;
  updated_at?: string;
  /** FRONT-512: set on rows from the recycle bin — drives the purge countdown. */
  deleted_at?: string | null;
  /** FRONT-451: reversible soft-archive (the Work board's Inactive state).
   * Non-null → writes on this entity 409 (FRONT-971's container freeze). */
  archived_at?: string | null;
  /** FRONT-1235: which of the two archive mechanisms set `archived_at` —
   * `"user"` (a person set this aside; stays in every memory read, see
   * `src/graph/archive-reason.ts`) or `"decay"` (faded out automatically).
   * Only meaningful when `archived_at` is set. */
  archive_reason?: string | null;
}

export interface GraphStats {
  nodes: number;
  edges: number;
  weekNew: number;
  weekFlagged: number;
  typeCounts: Record<string, number>;
  sourceCounts: Array<{ source: string; count: number }>;
}

/** FRONT-452 — see backend `src/graph/types.ts` (`GraphHealthRow`) for the
 * authoritative shape and the reasoning behind each field. */
export interface GraphHealthRow {
  userId: string;
  totalEntities: number;
  archivedEntities: number;
  unverifiedCount: number;
  unverifiedPct: number | null;
  neverReferencedCount: number;
  neverReferencedPct: number | null;
  deletedCount: number;
  deletionRatePct: number | null;
  sourceClassCounts: {
    user_stated: number;
    evidence_backed: number;
    fetched: number;
    model_derived: number;
  };
}

export interface GraphHealth {
  rows: GraphHealthRow[];
  /** FRONT-2528: display name (else email) per row's userId, same org only.
   *  An id with no recorded name is absent — show the id. Optional so an
   *  older backend without it just shows ids. */
  userNames?: Record<string, string>;
}

export interface ActivityItem {
  when: string;
  kind: 'create' | 'update' | 'flag';
  who: string;
  body: string;
  entity_id?: string;
  confidence?: string;
  /** FRONT-2486: the parts `body` is built from, for human wording. Absent on older servers. */
  entity_name?: string;
  category?: string;
  key?: string;
  value?: string;
  value_b?: string;
}

export interface ActivityGroup {
  date: string;
  items: ActivityItem[];
}

export interface SubgraphNode {
  id: string;
  name: string;
  type: string;
  pinned?: boolean;
  flagged?: boolean;
  focal?: boolean;
  source?: string;
  updatedAt?: string;
}

export interface SubgraphEdge {
  source: string;
  target: string;
  relation: string;
  fresh?: boolean;
  createdAt?: string;
}

export interface Subgraph {
  focal: string;
  nodes: SubgraphNode[];
  edges: SubgraphEdge[];
  truncated: boolean;
}

export interface GraphConflict {
  id: string;
  entity_id: string;
  entity_name: string;
  category: string;
  key: string;
  value_a: string;
  value_b: string;
  source_a?: string;
  source_b?: string;
  created_at: string;
  resolved_at?: string | null;
  resolved_value?: string | null;
}

/**
 * Paginated graph entities (ADR-0023). Cursor-based; ordered by
 * (updated_at desc, id desc). The Memory page renders one flat list and
 * shows a "Load more" button when more pages exist.
 */
/**
 * Memory page entity list. When `search` is non-empty the same endpoint
 * filters by name ILIKE; otherwise it returns the full list. Both paths
 * are paginated via the keyset cursor.
 */
/**
 * `archived` (FRONT-892): when true, fetches the archived set instead of the
 * active one — threaded into both the query string (`&archived=true`) and
 * the query key so the Work board's Active/Archived toggle caches each list
 * separately instead of colliding on one cache entry. Omitted from the
 * request entirely when false/undefined, so existing callers (the Memory
 * entities list) see byte-identical requests to before.
 *
 * `enabled` (FRONT-1114): forwarded straight to `usePaginatedList`/
 * `useInfiniteQuery` so a caller that only wants to query conditionally (the
 * person-name typeahead's debounce gate — no point firing a request for
 * every person in the graph before the typed text is long enough) doesn't
 * need a second query hook. Omitted (`undefined`) reproduces the prior
 * always-enabled behavior byte-for-byte.
 */
export function useGraphEntities(type?: string, search?: string, archived?: boolean, enabled?: boolean) {
  const trimmed = search?.trim() || undefined;
  return usePaginatedList<GraphEntity>(
    graphKeys.entities(type, trimmed, archived),
    '/api/graph/entities',
    { params: { type, q: trimmed, archived: archived ? 'true' : undefined }, enabled },
  );
}

/**
 * Legacy single-shot search hook. Kept for callers that still want a
 * top-N quick list — today, the Memory page's Graph tab quick-pick chip
 * list (the command palette uses `useWorkContainerSearch` for its Work section
 * and `useMemoryEntitySearch` for its Memory one — FRONT-1256, FRONT-876).
 * Prefer `useGraphEntities` with the `search` argument
 * for the Memory list — it paginates.
 */
export function useGraphSearch(query: string) {
  return useQuery({
    queryKey: graphKeys.search(query),
    queryFn: async () => {
      const data = await api.get<any>(`/api/graph/search?q=${encodeURIComponent(query)}`);
      return (Array.isArray(data) ? data : data.results ?? data.entities ?? []) as GraphEntity[];
    },
    enabled: query.length > 0,
  });
}

/**
 * FRONT-1256: the command palette's Work section — searches ONLY `project`
 * containers (never person/company/event/preference), capped at 5 hits, so
 * ⌘K can jump straight to a container's detail page. Same debounce-driven
 * enabled-when-non-empty shape as `useFileSearch` (`use-files.ts`).
 *
 * FRONT-2378: `routine` retired as a Work container kind — `types=` now
 * names `project` only. Sending the old, retired `routine` entry here would
 * 400 (`GET /api/graph/search` rejects any entry outside the live
 * `ENTITY_TYPES` vocabulary — `src/graph/routes.ts`).
 */
export function useWorkContainerSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: graphKeys.workSearch(q),
    queryFn: async () => {
      const data = await api.get<any>(
        `/api/graph/search?q=${encodeURIComponent(q)}&types=project&limit=5`,
      );
      return (Array.isArray(data) ? data : data.results ?? data.entities ?? []) as GraphEntity[];
    },
    enabled: q.length > 0,
  });
}

/**
 * FRONT-876: a `searchEntities` hit, as returned by `GET /api/graph/search` —
 * `matchedIn` says which of the three passes produced it: `"name"`,
 * `"alias"`, or `"attribute"`. See `src/graph/entities.ts`.
 */
export interface GraphEntitySearchHit extends GraphEntity {
  matchedIn: string;
}

/**
 * FRONT-876: the command palette's Memory section — searches ONLY the
 * knowledge-graph entity kinds that are NOT Work containers (`person`,
 * `company`, `event`, `preference` today — derived from the shared
 * `MEMORY_ENTITY_TYPES`, `~/lib/entity-types`, rather than hardcoded here, so
 * a kind added to the graph's vocabulary is picked up automatically instead
 * of requiring a second edit). `project` is the one Work container kind,
 * surfaced by the command palette's separate Work section instead
 * (FRONT-1256). Capped at 5 hits. Same debounce-driven enabled-when-non-empty
 * shape as `useFileSearch` (`use-files.ts`).
 */
export function useMemoryEntitySearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: graphKeys.memorySearch(q),
    queryFn: async () => {
      const data = await api.get<any>(
        `/api/graph/search?q=${encodeURIComponent(q)}&types=${MEMORY_ENTITY_TYPES.join(',')}&limit=5`,
      );
      return (Array.isArray(data) ? data : data.results ?? data.entities ?? []) as GraphEntitySearchHit[];
    },
    enabled: q.length > 0,
  });
}

export interface EntityAttributeRow {
  entity_id: string;
  category: string;
  key: string;
  value: string;
  source: string;
  confidence: 'high' | 'medium' | 'low';
  learned_at: string;
  /**
   * FRONT-617: the document this fact came out of, as a filename. Distinct
   * from `source` (what KIND of origin) and `provenance` (which conversation):
   * this points at the artefact. Absent on facts not derived from a document.
   */
  source_document?: string;
  provenance?: GraphProvenance;
}

export interface EntityRelationshipRow {
  id: string;
  source_type: string;
  source_id: string;
  source_name?: string;
  relation: string;
  target_type: string;
  target_id: string;
  target_name?: string;
  context: string;
  created_at: string;
  provenance?: GraphProvenance;
}

/**
 * FRONT-1265: an "also known as" entry — a name a past merge retired,
 * pointing back at the surviving entity. Mirrors `src/graph/aliases.ts`'s
 * `GraphEntityAliasRow` shape as returned (JSON-serialized) by
 * `GET /api/graph/entity/:id`.
 */
export interface EntityAliasRow {
  id: string;
  name: string;
  createdAt: string;
}

/**
 * `GET /api/graph/entity/:id` returns the full detail bundle — entity row
 * plus an array of attribute triples and an array of relationship rows. We
 * shape attributes as an array (not a Record) because keys repeat across
 * categories (e.g. `contact.email` and `social.email`).
 *
 * Extends `EntitySaveResponse` (`~/lib/entity-save-outcome`) rather than
 * redeclaring `stayedArchivedNote` here — one declaration, so a backend
 * rename of the field breaks both sides at compile time instead of neither.
 * `entity: GraphEntity` also satisfies that interface's optional, narrower
 * `entity?: { archived_at?: ... }` shape (FRONT-1154).
 */
export interface GraphEntityDetail extends EntitySaveResponse {
  entity: GraphEntity;
  attributes: EntityAttributeRow[];
  relationships: EntityRelationshipRow[];
  /** FRONT-1265: the entity's "also known as" names — see `EntityAliasRow`. */
  aliases: EntityAliasRow[];
  /** FRONT-1650/FRONT-2633: present only when a colleague shared this entity with the caller. */
  share?: ProjectShare;
}

export function useGraphEntity(id: string | null | undefined) {
  return useQuery({
    queryKey: graphKeys.entity(id ?? ''),
    queryFn: () => api.get<GraphEntityDetail>(`/api/graph/entity/${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}

export interface AutonomyRule {
  id: string;
  /** Backend-supplied human-readable title. Always present — defaults are seeded,
   * custom rules derive a title from their action slug. */
  name: string;
  description: string;
  tier: number;
  domain?: string;
  action?: string;
  isCustom?: boolean;
  /** FRONT-252: kept from the retired Manual level — this rule never runs
   *  unattended (no workflow switch, no desktop run grant). Cleared when the
   *  person picks a level. */
  noUnattended?: boolean;
}

export function useAutonomyRules() {
  return useQuery({
    queryKey: ['autonomy', 'rules'] as const,
    queryFn: async () => {
      const data = await api.get<any>('/api/autonomy/rules');
      return (Array.isArray(data) ? data : data.rules ?? []) as AutonomyRule[];
    },
  });
}

/**
 * FRONT-510 / FRONT-512: Finder-grade bulk delete, now a soft-delete into the
 * recycle bin. Sends the whole selection in one request; the backend rebuilds
 * the snapshot once and returns a `batchId` that powers the Undo toast (one
 * Undo restores exactly this delete's entities + cascaded relationships). On
 * success we invalidate the whole graph cache so the entity list, graph view,
 * header counts, and the Trash tab all refresh without a page reload.
 *
 * FRONT-1013: also invalidates the Work board cache (`workBoardKeys.all`),
 * mirroring `useDeleteEntity` below — project entities bulk-deleted from the
 * Work board's multi-select were leaving the board stale (the same
 * FRONT-1003-class gap `useDeleteEntity` already closed for single delete).
 * Harmless extra invalidation for the Memory page, which doesn't read the
 * Work board query.
 */
export function useBulkDeleteEntities() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ deleted: number; requested: number; batchId: string }>(
        '/api/graph/entities/bulk-delete',
        { ids },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: graphKeys.all });
      queryClient.invalidateQueries({ queryKey: workBoardKeys.all });
    },
  });
}

/**
 * FRONT-512: the trashed entities (the Memory recycle bin), newest first. A
 * flat list mirroring the Files/Inbox bins; each row carries `deleted_at` for
 * the "Purges in N days" countdown.
 */
export function useTrashedEntities(enabled = true) {
  return useQuery({
    queryKey: graphKeys.trash(),
    queryFn: async () => {
      const data = await api.get<{ items: GraphEntity[] }>('/api/graph/trash');
      return data.items ?? [];
    },
    enabled,
  });
}

/** FRONT-512: count for the Trash tab badge. */
export function useGraphTrashCount() {
  return useQuery({
    queryKey: graphKeys.trashCount(),
    queryFn: async () => {
      const data = await api.get<{ count: number }>('/api/graph/trash-count');
      return data.count;
    },
  });
}

/**
 * FRONT-512: restore from the recycle bin. `{ batchId }` is the Undo-toast path
 * (one delete's whole set); `{ ids }` is the Trash view's per-row / multi-select
 * restore. Invalidates the whole graph cache so list/graph/counts/trash refresh.
 *
 * FRONT-1013 code-review fix: also invalidates the Work board cache
 * (`workBoardKeys.all`), mirroring `useDeleteEntity`/`useBulkDeleteEntities`.
 * Without this, clicking Undo after a Work-board delete restored the entity
 * in the graph but the board itself (and its stat strip) stayed stale until a
 * reload.
 */
export function useRestoreTrash() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { batchId?: string; ids?: string[] }) =>
      api.post<{ restoredEntities: number; restoredRelationships: number }>(
        '/api/graph/trash/restore',
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: graphKeys.all });
      queryClient.invalidateQueries({ queryKey: workBoardKeys.all });
    },
  });
}

/**
 * FRONT-512: permanently delete from the recycle bin. `{ ids }` purges that
 * selection; `{ all: true }` empties the whole bin. Irreversible.
 */
export function usePurgeTrash() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) =>
      api.post<{ purged: number }>('/api/graph/trash/purge', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: graphKeys.all });
    },
  });
}

export function useGraphStats() {
  return useQuery({
    queryKey: graphKeys.stats(),
    queryFn: () => api.get<GraphStats>('/api/graph/stats'),
  });
}

export function useGraphHealth() {
  return useQuery({
    queryKey: graphKeys.health(),
    queryFn: () => api.get<GraphHealth>('/api/graph/health'),
  });
}

export function useGraphActivity(limit?: number) {
  return useQuery({
    queryKey: graphKeys.activity(limit),
    queryFn: async () => {
      const url = limit ? `/api/graph/activity?limit=${limit}` : '/api/graph/activity';
      return api.get<ActivityGroup[]>(url);
    },
  });
}

export function useGraphSubgraph(entityId: string | null) {
  return useQuery({
    queryKey: graphKeys.subgraph(entityId ?? ''),
    queryFn: () => api.get<Subgraph>(`/api/graph/subgraph/${encodeURIComponent(entityId!)}`),
    enabled: !!entityId,
  });
}

/**
 * Paginated graph conflicts (ADR-0023). Cursor-based; ordered by
 * (created_at desc, id desc). The Conflicts modal cycles through every
 * conflict, so we auto-drain pages via `fetchNextPage` from the caller.
 */
export function useGraphConflicts() {
  return usePaginatedList<GraphConflict>(graphKeys.conflicts(), '/api/graph/conflicts');
}

export function useGraphConflictCount() {
  return useQuery({
    queryKey: graphKeys.conflictCount(),
    queryFn: async () => {
      const data = await api.get<{ count: number }>('/api/graph/conflict-count');
      return data.count;
    },
  });
}
